import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Truck, Plus, Package, Clock, CheckCircle, XCircle, 
  MapPin, Star, User, LogOut, Menu, X, ChevronRight, AlertCircle, Shield
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700',
  accepted: 'bg-blue-100 text-blue-700',
  picked_up: 'bg-purple-100 text-purple-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700'
};

const statusLabels = {
  pending: 'Aguardando',
  accepted: 'Aceito',
  picked_up: 'Em trânsito',
  delivered: 'Entregue',
  cancelled: 'Cancelado'
};

export const CustomerDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
  }, [token]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const activeOrders = orders.filter(o => ['pending', 'accepted', 'picked_up'].includes(o.status));
  const completedOrders = orders.filter(o => ['delivered', 'cancelled'].includes(o.status));

  return (
    <div className="min-h-screen bg-gray-100" data-testid="customer-dashboard">
      {/* Mobile Sidebar Toggle */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 bg-white p-2 rounded-xl shadow-lg"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="sidebar-toggle"
      >
        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-white shadow-xl transform transition-transform duration-300 lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="p-6">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-tuggo-mint rounded-xl flex items-center justify-center">
              <Truck className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">TugGo</span>
          </Link>
        </div>

        <nav className="px-4 space-y-2">
          <Link
            to="/dashboard"
            className="flex items-center gap-3 px-4 py-3 rounded-xl bg-tuggo-mint/10 text-tuggo-mint font-medium"
          >
            <Package className="w-5 h-5" />
            Os Meus Pedidos
          </Link>
          <Link
            to="/new-order"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-600 hover:bg-gray-100"
          >
            <Plus className="w-5 h-5" />
            Nova Entrega
          </Link>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200">
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-gray-600" />
            </div>
            <div className="flex-1">
              <div className="font-medium text-gray-900 truncate">{user?.name}</div>
              <div className="text-sm text-gray-500">Cliente</div>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-red-600 hover:bg-red-50"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 pt-16 lg:pt-6">
        <div className="max-w-5xl mx-auto">
          {/* Verification Banner */}
          {user && !user.is_verified && (
            <Link 
              to="/verification/customer"
              className="block bg-yellow-50 border border-yellow-200 rounded-2xl p-4 mb-6 hover:bg-yellow-100 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <Shield className="w-6 h-6 text-yellow-600" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-yellow-800">Complete a sua verificação</div>
                  <div className="text-sm text-yellow-600">
                    {user.verification_status === 'submitted' 
                      ? 'Os seus documentos estão em análise'
                      : 'Verifique a sua identidade para utilizar todas as funcionalidades'}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-yellow-600" />
              </div>
            </Link>
          )}

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Olá, {user?.name?.split(' ')[0]}!</h1>
              <p className="text-gray-600">Gerencie as suas entregas</p>
            </div>
            <Link to="/new-order">
              <Button className="bg-tuggo-mint text-white hover:bg-tuggo-mint/90 rounded-full" data-testid="new-order-btn">
                <Plus className="w-5 h-5 mr-2" />
                Nova Entrega
              </Button>
            </Link>
          </div>

          {/* Active Orders */}
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-tuggo-mint" />
              Entregas Ativas ({activeOrders.length})
            </h2>
            {loading ? (
              <div className="bg-white rounded-2xl p-8 text-center text-gray-500">
                A carregar...
              </div>
            ) : activeOrders.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Nenhuma entrega ativa</p>
                <Link to="/new-order" className="text-tuggo-mint font-medium hover:underline">
                  Criar nova entrega
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {activeOrders.map((order) => {
                  const isHourly = order.order_type === 'hourly';
                  return (
                    <Link
                      key={order.id}
                      to={`/orders/${order.id}`}
                      className="block bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
                      data-testid={`order-card-${order.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[order.status]}`}>
                              {statusLabels[order.status]}
                            </span>
                            {isHourly && (
                              <span className="px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-700 flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {order.rental_hours}h
                              </span>
                            )}
                            <span className="text-sm text-gray-500">{order.category}</span>
                          </div>
                          <p className="text-gray-900 font-medium mb-2">{order.description}</p>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <MapPin className="w-4 h-4" />
                            {isHourly ? (
                              order.rental_base_location?.city || 'Localização base'
                            ) : (
                              `${order.delivery_address.neighborhood}, ${order.delivery_address.city}`
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-tuggo-mint">
                            € {order.estimated_price.toFixed(2)}
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400 ml-auto mt-2" />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* Completed Orders */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Histórico ({completedOrders.length})
            </h2>
            {completedOrders.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 text-center">
                <p className="text-gray-500">Nenhuma entrega concluída ainda</p>
              </div>
            ) : (
              <div className="space-y-4">
                {completedOrders.slice(0, 5).map((order) => (
                  <Link
                    key={order.id}
                    to={`/orders/${order.id}`}
                    className="block bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow opacity-75"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[order.status]}`}>
                            {statusLabels[order.status]}
                          </span>
                        </div>
                        <p className="text-gray-700">{order.description}</p>
                      </div>
                      <div className="text-lg font-bold text-gray-600">
                        € {(order.final_price || order.estimated_price).toFixed(2)}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};
